/*
 * Money, in one place.
 *
 * **Every amount in this application is an integer number of cents.** Never a
 * float, never a decimal — CLAUDE.md §4. `12500` is $125.00 and there is no
 * other representation of it anywhere in the code.
 *
 * Formatting happens at the display edge only, which is what this file is for.
 * The moment a price becomes the string "$125.00" it has stopped being a number
 * you can add up, and the whole point of integer cents is that adding them up
 * is exact.
 */

/** `12500` → `"$125.00"`. */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * `12500` → `"125.00"`. What goes in a price input.
 *
 * No symbol and no thousands separator, because this is a value being edited
 * rather than one being read, and `$1,250.00` is not something an input can
 * parse back.
 */
export function centsToInput(cents: number | null): string {
  if (cents === null) return '';
  return (cents / 100).toFixed(2);
}

export type MoneyParseResult = { ok: true; cents: number | null } | { ok: false; message: string };

/**
 * Turn what somebody typed into cents.
 *
 * Deliberately strict about one thing: **more than two decimal places is
 * rejected, not rounded.** Someone typing `19.999` either meant something this
 * field cannot hold or made a mistake, and silently storing `$20.00` is the
 * version of that they find out about on an invoice.
 *
 * Everything else is forgiving — a leading `$`, thousands separators, spaces,
 * and an empty string, which means "no price" rather than zero. Those are
 * different: "Other / Custom" has no price, and a service included at no charge
 * is `0`.
 */
export function parseMoneyInput(raw: string): MoneyParseResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, cents: null };

  const cleaned = trimmed.replace(/[$,\s]/g, '');

  if (!/^-?\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.') {
    return { ok: false, message: 'Enter an amount like 125 or 125.50' };
  }

  if (cleaned.startsWith('-')) {
    return { ok: false, message: 'A price cannot be negative' };
  }

  const [whole = '', fraction = ''] = cleaned.split('.');

  if (fraction.length > 2) {
    return { ok: false, message: 'Amounts go to two decimal places — 125.50, not 125.499' };
  }

  // Built from the digits rather than by multiplying a float. `19.99 * 100` is
  // 1998.9999999999998 in IEEE 754, and `Math.round` hides that until the day
  // it does not.
  const cents = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0') || '0');

  if (!Number.isSafeInteger(cents)) {
    return { ok: false, message: 'That amount is too large' };
  }

  return { ok: true, cents };
}

/** A line total: `flat` is the price once, `hourly` is rate × hours. */
export function lineTotalCents(
  pricingUnit: 'flat' | 'hourly',
  unitPriceCents: number,
  quantity: number,
): number {
  if (pricingUnit === 'flat') return unitPriceCents * Math.round(quantity);

  // 2.5 hours at $45/hr is 11250 cents. Rounded to the cent at the end, once —
  // rounding each step compounds the error across a multi-line invoice.
  return Math.round(unitPriceCents * quantity);
}
